import { supabase } from "@/lib/supabaseClient";

export type Lesson = {
  id: string;
  courseId: string;
  order: number;
  title: string;
  videoKey: string | null;
  durationSeconds: number | null;
};

export type LessonVideo = {
  url: string;
  lessonId: string;
  courseId: string;
  title: string;
  expiresIn: number;
};

/**
 * % de progreso por curso para el usuario autenticado. Lee la tabla
 * course_progress, que se recalcula sola (vía trigger en la base) cada vez
 * que cambia lesson_progress -- nunca se escribe directo desde el cliente.
 */
export async function fetchCourseProgressMap(): Promise<Map<string, number>> {
  const { data, error } = await supabase.from("course_progress").select("course_id, percent");
  if (error) {
    console.error("fetchCourseProgressMap error", error);
    return new Map();
  }
  return new Map((data ?? []).map((row) => [row.course_id as string, row.percent as number]));
}

/** Catálogo de lecciones de un curso, en orden. */
export async function fetchLessons(courseId: string): Promise<Lesson[]> {
  const { data, error } = await supabase
    .from("lessons")
    .select("id, course_id, lesson_order, title, video_key, duration_seconds")
    .eq("course_id", courseId)
    .order("lesson_order", { ascending: true });
  if (error) {
    console.error("fetchLessons error", error);
    return [];
  }
  return (data ?? []).map((r) => ({
    id: r.id as string,
    courseId: r.course_id as string,
    order: r.lesson_order as number,
    title: r.title as string,
    videoKey: r.video_key as string | null,
    durationSeconds: r.duration_seconds as number | null,
  }));
}

/** IDs de lecciones ya completadas por el usuario, para un curso dado. */
export async function fetchCompletedLessonIds(courseId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("lesson_progress")
    .select("lesson_id, completed")
    .eq("course_id", courseId)
    .eq("completed", true);
  if (error) {
    console.error("fetchCompletedLessonIds error", error);
    return new Set();
  }
  return new Set((data ?? []).map((r) => r.lesson_id as string));
}

/**
 * Marca una lección como completada (o actualiza segundos vistos sin
 * completarla). El trigger `recalc_course_progress` en Supabase recalcula
 * automáticamente el % del curso -- no hay que tocar course_progress a mano.
 */
export async function markLessonProgress(
  courseId: string,
  lessonId: string,
  opts: { completed?: boolean; secondsWatched?: number } = {}
): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) return false;

  const { error } = await supabase.from("lesson_progress").upsert(
    {
      user_id: userId,
      course_id: courseId,
      lesson_id: lessonId,
      completed: opts.completed ?? true,
      seconds_watched: opts.secondsWatched ?? 0,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,lesson_id" }
  );
  if (error) {
    console.error("markLessonProgress error", error);
    return false;
  }
  return true;
}

/**
 * Pide a la Edge Function "r2-video" una URL firmada y temporal (1h) para
 * reproducir el video de una lección, alojado en Cloudflare R2. Devuelve
 * null si el video todavía no está disponible (p.ej. R2 sin configurar, o
 * la lección aún no tiene archivo subido).
 */
export async function fetchLessonVideoUrl(lessonId: string): Promise<LessonVideo | null> {
  const { data, error } = await supabase.functions.invoke("r2-video", { body: { lessonId } });
  if (error) {
    console.warn("fetchLessonVideoUrl: video no disponible todavia", error.message ?? error);
    return null;
  }
  if (!data || data.error) {
    if (data?.error && data.error !== "r2_not_configured") {
      console.warn("fetchLessonVideoUrl error", data.error);
    }
    return null;
  }
  return data as LessonVideo;
}
